import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import _ from 'lodash';

import { AddIcon, DraftsIcon } from '@treams/treams-icons';
import {
	Button,
	Card,
	Container,
	Heading,
	Jumbo,
	Paragraph,
	Separator,
	Skeleton,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

import GoalsList from './GoalsList';
import GoalsTabs from './GoalsTabs';

const Background = styled(Container)`
	background: ${(props) => props.theme.colors.whiteBlue};
`;

function GoalDriverCard(props: { driver: graphql.GoalDriver }) {
	const { localize } = useLocalize();

	return (
		<Card>
			<Paragraph>{localize(props.driver.title)?.text}</Paragraph>
		</Card>
	);
}

GoalDriverCard.Skeleton = function GoalDriverCardSkeleton() {
	return (
		<Card.Skeleton>
			<Paragraph.Skeleton lines={2} />
		</Card.Skeleton>
	);
};

function GoalDrivers() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useGoalDriversQuery({
		variables: { isArchived: false },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			<Flex alignItems="center" mb={3}>
				<Heading flexGrow={1} flexShrink={1}>
					{t('goals.collective.goalDrivers')}
				</Heading>
				{query.data?.viewer.workspace.viewerCanCreateGoalDrivers && (
					<Button
						ml={2}
						icon={<DraftsIcon />}
						title={t('goals.index.toolbar.drivers')}
						link={`/settings/organization/drivers`}
						small
					/>
				)}
			</Flex>
			{_.sortBy(query.data?.goalDrivers, (driver) => {
				return localize(driver.title).text;
			}).map((driver) => (
				<Box key={driver.id} mb={2}>
					<GoalDriverCard driver={driver as graphql.GoalDriver} />
				</Box>
			)) ?? (
				<Skeleton.Repeat count={3}>
					<Box mb={2}>
						<GoalDriverCard.Skeleton />
					</Box>
				</Skeleton.Repeat>
			)}
		</Box>
	);
}

export function OrganizationGoalsList(props: {
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
}) {
	const { t } = useTranslation();

	const workspace = useWorkspace();

	const query = graphql.useWorkspaceGoalsQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Active, last: 100 },
		fetchPolicy: 'network-only',
	});
	const completedQuery = graphql.useWorkspaceGoalsQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Completed, last: 100 },
		fetchPolicy: 'network-only',
	});
	const archivedQuery = graphql.useWorkspaceGoalsLazyQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Archived, last: 100 },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			<Flex alignItems="center">
				<Heading flexGrow={1} flexShrink={1} mb={2}>
					{t('goals.collective.organizationGoals')}
				</Heading>
				{query.data?.workspace.viewerCanCreateGoal && !props.onSelect && (
					<Button
						ml={2}
						icon={<AddIcon />}
						title={t('goals.index.toolbar.new')}
						link={`/goals/new?workspace_id=${workspace.id}`}
						small
					/>
				)}
			</Flex>
			<GoalsList
				query={query}
				path="workspace.goalsV2"
				completedQuery={completedQuery}
				archivedQuery={
					query.data?.workspace.viewerCanViewArchivedGoals && !props.hideArchived
						? archivedQuery
						: undefined
				}
				user={undefined}
				onSelect={props.onSelect}
				selectedGoal={props.selectedGoal}
				reorderable={query.data?.workspace.viewerCanCreateGoal}
			/>
		</Box>
	);
}

export default function OrganizationGoals() {
	const { t } = useTranslation();

	return (
		<Box>
			<Jumbo
				title={t('goals.index.title')}
				description={t('goals.index.description')}
				footer={<GoalsTabs />}
			/>
			<Background py={4} minHeight="100vh">
				<GoalDrivers />
				<Separator my={4} />
				<OrganizationGoalsList />
			</Background>
		</Box>
	);
}
